/**
 * @module EmptyNotifications
 * @package @geenius/notifications-react
 * @description Renders the empty-state used when there are no notifications to
 * display in the React Tailwind variant.
 */

import { FadeIn } from '@geenius/motion/react'
import type { JSX } from 'react'
import { useNotificationCopy } from '../lib/notificationCopy'
import { cn } from '../lib/utils'

/**
 * Renders the empty notification state.
 *
 * @returns The empty-state markup.
 */
export interface EmptyNotificationsProps {
  className?: string
}

/**
 * Renders the empty notification state.
 *
 * @param props Optional style override for the empty-state container.
 * @returns The empty-state markup.
 */
export function EmptyNotifications({
  className,
}: EmptyNotificationsProps): JSX.Element {
  const { t } = useNotificationCopy()

  return (
    <FadeIn
      className={cn(
        'flex flex-col items-center justify-center px-4 py-16 text-center',
        className,
      )}
    >
      <span aria-hidden="true" className="mb-3 text-5xl opacity-20">
        🔔
      </span>
      <div className="mb-1 text-[15px] font-medium text-foreground">
        {t('notifications.empty.title')}
      </div>
      <div className="text-xs text-muted-foreground">
        {t('notifications.empty.description')}
      </div>
    </FadeIn>
  )
}
